// ---------------------------------------------------------------------------
// PWA_ControlBillet — Fixed top header bar
// Shows vigile name, event ID, connection status, scan counter, and logout.
// ---------------------------------------------------------------------------

import { useConfigStore } from '@/store/config-store';
import { useAppStore } from '@/store/app-store';
import { useScanStore } from '@/store/scan-store';
import { useAuthStore } from '@/store/auth-store';

function ShieldIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="text-accent-light shrink-0"
    >
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );
}

function LogoutIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  );
}

export function Header() {
  const vigileId = useConfigStore((s) => s.vigileId);
  const eventId = useConfigStore((s) => s.eventId);
  const connectionStatus = useAppStore((s) => s.connectionStatus);
  const totalScans = useScanStore((s) => s.todayStats.total);
  const logout = useAuthStore((s) => s.logout);

  const isOnline = connectionStatus === 'online';

  function handleLogout() {
    if (window.confirm('Se deconnecter ?')) {
      logout();
    }
  }

  return (
    <header className="fixed top-0 left-0 right-0 h-14 z-40 bg-surface-light border-b border-white/10 flex items-center justify-between px-4">
      {/* Left: Shield + Vigile name */}
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <ShieldIcon />
        <span className="text-sm font-medium text-white truncate">
          {vigileId || 'Vigile'}
        </span>
      </div>

      {/* Center: Event ID badge */}
      <div className="flex items-center justify-center">
        {eventId && (
          <span className="bg-accent/20 text-accent-light text-xs font-semibold px-3 py-1 rounded-full">
            Event #{eventId}
          </span>
        )}
      </div>

      {/* Right: Connection dot + Scan counter + Logout */}
      <div className="flex items-center gap-3 justify-end flex-1">
        {/* Connection status dot */}
        <div className="flex items-center gap-1.5">
          <div
            className={`w-2.5 h-2.5 rounded-full ${
              isOnline ? 'bg-success' : 'bg-danger'
            }`}
          />
          <span className="text-xs text-gray-400 hidden min-[360px]:inline">
            {isOnline ? 'En ligne' : 'Hors ligne'}
          </span>
        </div>

        {/* Scan counter badge */}
        <div className="bg-surface-card rounded-full px-2.5 py-0.5 flex items-center gap-1">
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            className="text-gray-400"
          >
            <rect x="3" y="3" width="7" height="7" rx="1" />
            <rect x="14" y="3" width="7" height="7" rx="1" />
            <rect x="3" y="14" width="7" height="7" rx="1" />
            <rect x="14" y="14" width="7" height="7" rx="1" />
          </svg>
          <span className="text-xs font-bold text-white">{totalScans}</span>
        </div>

        {/* Logout button */}
        <button
          type="button"
          onClick={handleLogout}
          className="text-gray-400 hover:text-danger transition-colors p-1"
          title="Se deconnecter"
        >
          <LogoutIcon />
        </button>
      </div>
    </header>
  );
}
